import { fm } from '@/appLogic/fileManager/baseFileManager';
import { TranslateInputFileManager } from '@/appLogic/fileManager/translate/translateInputFileManager';
import { ExcelRow } from '@/coreDomainsModule/translateExcel';
import XLSX from 'xlsx';
import { HeaderCandidate, UpsertBusinessError } from '../pages/interfaces/UploadFile';
import { UNSELECTED_HEADER_VALUE } from '../constants';

/**
 * エクセルファイル周りのロジックをまとめたクラス
 */
export class ReadFileService {
  /**
   * ヘッダー候補一覧を読み込む
   * @param file - ファイル
   * @param headerNumber - ヘッダー番号
   * @returns [ヘッダー候補, あとで登録する用のデータ(現状、在庫表のみ想定)]
   */
  public static readHeaderCandidate = async (file: File, headerNumber: number): Promise<[HeaderCandidate[], ExcelRow[] | undefined]> => {
    const data = await fm.convertFileToBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const excelData: ExcelRow[] = new TranslateInputFileManager().parseExcelObjects(sheet, headerNumber, '', undefined);

    const excelRow = excelData?.[0];
    if (!excelRow) {
      return [[], undefined];
    }

    const candidateHeaders = Object.keys(excelRow)
      .filter((key) => {
        // xxx_1, xxx_2, xxx_3, ... という形式のヘッダーは候補として除外する
        return !key.match(/_\d+$/);
      })
      .map((key) => {
        return {
          value: key,
          alreadySelected: false,
        };
      });

    candidateHeaders.unshift({
      value: UNSELECTED_HEADER_VALUE,
      alreadySelected: false,
    });

    return [candidateHeaders, excelData];
  };

  /**
   * vue3-dropzoneがこの機能を持っていなかったので仕方なく作っている
   * fileサイズを適切な文字列に変換する
   * @param file - ファイル
   */
  public static getFileSizeStr = (file: any): string => {
    const fileSize: number = file?.size ?? -1;
    if (fileSize === -1) return '';
    const kb = 1024;
    const mb = Math.pow(kb, 2);
    const gb = Math.pow(kb, 3);
    const tb = Math.pow(kb, 4);
    let unit = 'byte';
    let target = 1;
    if (fileSize >= tb) {
      target = tb;
      unit = 'TB';
    } else if (fileSize >= gb) {
      target = gb;
      unit = 'GB';
    } else if (fileSize >= mb) {
      target = mb;
      unit = 'MB';
    } else if (fileSize >= kb) {
      target = kb;
      unit = 'KB';
    } else {
      target = 1;
      unit = 'BYTE';
    }
    const res = target !== null ? Math.floor((fileSize / target) * 100) / 100 : fileSize;
    // 単位を付けて出力
    return res.toFixed(1) + unit;
  };

  /**
   * Event型から安全にnameを取り出す
   * @param file - イベント
   */
  public static fetchNameForEvent = (file: Event): string => {
    if (file instanceof File) {
      return file.name;
    }
    return '';
  };

  /**
   * エラーのcsvデータを作成する
   * @param upsertBusinessError - エラー情報
   * @returns エラーのcsvデータ
   */
  public static createUpsertBusinessesFromExcelErrorCsvData = (upsertBusinessError: UpsertBusinessError[]): string[][] => {
    const csvData: string[][] = [];
    csvData.push(['対象セル（行）', 'エラー名', '詳細']); // STOPSHIP: エラーのcsv周りのi18n
    upsertBusinessError.forEach((error) => {
      const errorAddress =
        error.error === 'RequiredRow' ||
        error.error === 'NotFoundAggregate' ||
        error.error === 'DuplicateRow' ||
        error.error === 'NotExistAggregateBusiness'
          ? `${error.cellAddress.replace(/[^0-9]/g, '').toString()}(Line)` // 行番号のみ取得
          : error.cellAddress + '(Address)'; // セルの番地をそのまま取得

      csvData.push([errorAddress, error.error, error.message]);
    });
    return csvData;
  };
}
